import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, X, Info } from 'lucide-react';
import ClientDirectorsSection from './ClientDirectorsSection';

const CLIENT_TAGS = ['High Value', 'Startup', 'SME', 'Large Enterprise', 'Seasonal', 'Non-Profit', 'Government', 'Active', 'Inactive', 'Priority'];

export const ClientFormDialog = ({
  open, onOpenChange, editMode, form, setForm,
  onSubmit, formError, submitting, canManage,
  addGstin, removeGstin, updateGstin, toggleService, toggleTag,
  serviceConfigs = [], editingClientId = null,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{editMode ? 'Edit Client' : 'Add New Client'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        {formError && (
          <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Genius S.No.</Label>
            <Input value={form.serial_no || ''} onChange={e => setForm({...form, serial_no: e.target.value})} placeholder="e.g. GN-001 or ABC123" data-testid="client-serial-no-input" />
          </div>
          <div>
            <Label>Client Name *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="client-name-input" />
          </div>
        </div>

        <div>
          <Label>Company Name</Label>
          <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="client-company-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Status (Legal)</Label>
            <select
              value={form.legal_status || ''}
              onChange={e => setForm({...form, legal_status: e.target.value})}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="client-legal-status"
            >
              <option value="">Select...</option>
              <option value="Individual / Proprietor">Individual / Proprietor</option>
              <option value="Partnership Firm">Partnership Firm</option>
              <option value="LLP">LLP</option>
              <option value="Private Limited">Private Limited</option>
              <option value="Public Limited">Public Limited</option>
              <option value="HUF">HUF</option>
              <option value="Society">Society</option>
              <option value="Trust">Trust</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <Label>DOB / DOI</Label>
            <Input type="date" value={form.dob_doi || ''} onChange={e => setForm({...form, dob_doi: e.target.value})} data-testid="client-dob-doi" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="client-email-input" />
          </div>
          <div>
            <Label>Mobile Number</Label>
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91-9XXXXXXXXX" data-testid="client-phone-input" />
          </div>
        </div>

        <Separator />

        <div>
          <Label className="text-sm font-medium">Unique ID (PAN or TAN required) *</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">PAN Number</Label>
              <Input value={form.pan} onChange={e => setForm({...form, pan: e.target.value.toUpperCase()})} placeholder="ABCDE1234F" maxLength={10} data-testid="client-pan-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">TAN Number</Label>
              <Input value={form.tan} onChange={e => setForm({...form, tan: e.target.value.toUpperCase()})} placeholder="MUMA12345B" maxLength={10} data-testid="client-tan-input" />
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">GSTIN(s)</Label>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={addGstin} data-testid="add-gstin-button">
              <Plus className="h-3 w-3 mr-1" /> Add GSTIN
            </Button>
          </div>
          <div className="space-y-2">
            {form.gstins.map((gstin, idx) => (
              <div key={`gstin-field-${idx}`} className="space-y-1.5 p-2 rounded-md border border-dashed border-muted-foreground/20">
                <div className="flex items-center gap-2">
                  <Input value={gstin.number || ''} onChange={e => updateGstin(idx, 'number', e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" maxLength={15} className="font-mono text-sm flex-1" data-testid={`gstin-input-${idx}`} />
                  <select
                    value={gstin.category || 'Regular'}
                    onChange={e => updateGstin(idx, 'category', e.target.value)}
                    className="flex h-9 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring w-[130px]"
                    data-testid={`gstin-category-${idx}`}
                  >
                    <option value="Regular">Regular</option>
                    <option value="Tax Deductor">Tax Deductor</option>
                    <option value="ISD">ISD</option>
                    <option value="Composition">Composition</option>
                  </select>
                  {form.gstins.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" onClick={() => removeGstin(idx)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={`gstin-reg-${idx}`} className="text-[10px] text-muted-foreground whitespace-nowrap">Regd:</Label>
                    <Input
                      id={`gstin-reg-${idx}`}
                      type="date"
                      value={gstin.registration_date || ''}
                      onChange={e => updateGstin(idx, 'registration_date', e.target.value)}
                      className="h-7 text-xs"
                      data-testid={`gstin-regdate-${idx}`}
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={`gstin-canc-${idx}`} className="text-[10px] text-muted-foreground whitespace-nowrap">Cancelled:</Label>
                    <Input
                      id={`gstin-canc-${idx}`}
                      type="date"
                      value={gstin.cancellation_date || ''}
                      onChange={e => updateGstin(idx, 'cancellation_date', e.target.value)}
                      className="h-7 text-xs"
                      data-testid={`gstin-cancdate-${idx}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {editMode && (
          <>
            <ClientDirectorsSection clientId={editingClientId} />
            <Separator />
          </>
        )}

        {canManage && (
          <div>
            <Label className="text-sm font-medium mb-2 block">Services</Label>
            <div className="grid grid-cols-2 gap-2">
              <TooltipProvider delayDuration={300}>
                {serviceConfigs.map(sc => (
                  <Tooltip key={sc.id}>
                    <TooltipTrigger asChild>
                      <label className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${form.services.includes(sc.name) ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`} data-testid={`service-checkbox-${sc.name}`}>
                        <Checkbox checked={form.services.includes(sc.name)} onCheckedChange={() => toggleService(sc.name)} />
                        <span className="text-sm flex-1">{sc.name}</span>
                        {sc.subtask_titles.length > 0 && <Info className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />}
                      </label>
                    </TooltipTrigger>
                    {sc.subtask_titles.length > 0 && (
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="font-semibold text-xs mb-1">Linked Subtasks:</p>
                        <ul className="text-xs space-y-0.5">
                          {sc.subtask_titles.map(t => <li key={t}>{t}</li>)}
                        </ul>
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </TooltipProvider>
            </div>
          </div>
        )}

        <div>
          <Label>Address</Label>
          <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
        </div>

        <div>
          <Label className="text-sm font-medium mb-2 block">Client Classification</Label>
          <div className="flex flex-wrap gap-2">
            {CLIENT_TAGS.map(tag => (
              <Badge key={tag} variant={form.tags.includes(tag) ? "default" : "outline"} className="cursor-pointer hover:scale-105 transition-transform" onClick={() => toggleTag(tag)}>
                {tag}
              </Badge>
            ))}
          </div>
          {form.tags.length > 0 && <div className="mt-2 text-xs text-muted-foreground">Selected: {form.tags.join(', ')}</div>}
        </div>

        <div>
          <Label htmlFor="notes">Notes & Remarks <span className="text-xs text-muted-foreground">(Internal use only)</span></Label>
          <Textarea id="notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Add internal notes about this client..." rows={3} className="resize-none" />
        </div>

        <Button type="submit" className="w-full" disabled={submitting} data-testid="client-create-submit">
          {submitting ? 'Saving...' : (editMode ? 'Update Client' : 'Create Client')}
        </Button>
      </form>
    </DialogContent>
  </Dialog>
);
